import { FormControl, FormControlProps, Stack, TextField, Typography } from '@mui/material'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { DatePicker as DatePickerMUI } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { Field, FieldConfig, FieldProps } from 'formik'
import { useEffect, useState } from 'react'
import { Label } from '../ui/Label'
import { LabelError } from './LabelError'

/**
 * @typedef {object} IDatePickerProps
 * @property {string} [label]
 * @property {boolean} [requiredLabel]
 * @property {string} [labelEndDate]
 * @property {string} [placeholder]
 * @property {string} [placeholderEndDate]
 * @property {boolean} [labelOutline]
 * @property {FormControlProps} [propsContainer]
 * @property {boolean} [range]
 * @property {Date} [date]
 * @property {Date} [minDate]
 * @property {Date} [maxDate]
 */
/**
 * @param {IDatePickerProps & FieldProps} props
 */
export const DatePicker = (props) => {
  const {
    label,
    requiredLabel,
    labelEndDate,
    labelOutline,
    placeholder,
    placeholderEndDate,
    propsContainer,
    date: _date,
    range: isRange,
    minDate,
    maxDate,

    field,
    form,
    meta,
  } = props
  const [date, setDate] = useState(field !== undefined ? field.value : _date)
  const [startDate, setStartDate] = useState()
  const [endDate, setEndDate] = useState()

  useEffect(() => {
    const v = field !== undefined ? field.value : date
    if (!isRange) setDate(v)
    else if ([null, undefined].includes(v)) {
      setStartDate(undefined)
      setEndDate(undefined)
    } else {
      setStartDate(v[0])
      setEndDate(v[1])
    }
  }, [field?.value, _date])

  const onChangeFormValue = (v) => {
    if (form) form?.setFieldValue(field.name, v)
    else setDate(v)
  }

  const touched = form?.touched[field.name]
  const error = form?.errors[field.name]
  const isError = touched === true && !!error

  const renderInput = (params, label, placeholder) => (
    <TextField
      fullWidth
      size="small"
      {...params}
      {...(!labelOutline && { label: label })}
      error={isError}
      placeholder={placeholder}
    />
  )
  const components = {
    OpenPickerIcon: () => (
      <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M5.33333 3.33252C5.33333 3.60585 5.56 3.83252 5.83333 3.83252C6.10667 3.83252 6.33333 3.60585 6.33333 3.33252V2.83252H10.6667V3.33252C10.6667 3.60585 10.8933 3.83252 11.1667 3.83252C11.44 3.83252 11.6667 3.60585 11.6667 3.33252V2.85595C13.203 3.00756 13.9679 3.89609 13.999 5.55908H3.00099C3.03208 3.89609 3.79702 3.00756 5.33333 2.85595V3.33252ZM6.33333 1.83252V1.33252C6.33333 1.05919 6.10667 0.83252 5.83333 0.83252C5.56 0.83252 5.33333 1.05919 5.33333 1.33252V1.85334C3.20922 2.03544 2 3.40438 2 5.66585V11.3325C2 13.7659 3.4 15.1659 5.83333 15.1659H11.1667C13.6 15.1659 15 13.7659 15 11.3325V5.66585C15 3.40438 13.7908 2.03544 11.6667 1.85334V1.33252C11.6667 1.05919 11.44 0.83252 11.1667 0.83252C10.8933 0.83252 10.6667 1.05919 10.6667 1.33252V1.83252H6.33333ZM3 6.55908V11.3325C3 13.2392 3.92667 14.1659 5.83333 14.1659H11.1667C13.0733 14.1659 14 13.2392 14 11.3325V6.55908H3ZM6.16667 9.66606C6.08 9.66606 5.99333 9.64607 5.91333 9.61274C5.82667 9.5794 5.76 9.53272 5.69333 9.47272C5.57333 9.34606 5.5 9.17939 5.5 8.99939C5.5 8.91273 5.52 8.82606 5.55333 8.74606C5.58667 8.66606 5.63333 8.59273 5.69333 8.52607C5.76 8.46607 5.82667 8.41938 5.91333 8.38605C6.15333 8.28605 6.45333 8.3394 6.64 8.52607C6.76 8.65273 6.83333 8.82606 6.83333 8.99939C6.83333 9.03939 6.82667 9.08607 6.82 9.13274C6.81333 9.17274 6.8 9.21273 6.78 9.25273C6.76667 9.29273 6.74667 9.33273 6.72 9.37273C6.70667 9.39494 6.68742 9.41716 6.66816 9.43938L6.66815 9.43938C6.65852 9.45049 6.64889 9.46161 6.64 9.47272C6.51333 9.59272 6.34 9.66606 6.16667 9.66606ZM8.24667 9.61244C8.32667 9.64578 8.41333 9.66577 8.5 9.66577C8.67333 9.66577 8.84667 9.59243 8.97333 9.47243C8.98222 9.46132 8.99186 9.4502 9.00149 9.43909L9.0015 9.43908C9.02075 9.41687 9.04 9.39465 9.05333 9.37243C9.08 9.33243 9.1 9.29244 9.11333 9.25244C9.13333 9.21244 9.14667 9.17244 9.15333 9.13244C9.16 9.08578 9.16667 9.0391 9.16667 8.9991C9.16667 8.82577 9.09333 8.65244 8.97333 8.52577C8.78667 8.33911 8.48667 8.27909 8.24667 8.38576C8.16 8.41909 8.09333 8.46577 8.02667 8.52577C7.96667 8.59244 7.92 8.66576 7.88667 8.74576C7.85333 8.82577 7.83333 8.91244 7.83333 8.9991C7.83333 9.1791 7.90667 9.34576 8.02667 9.47243C8.09333 9.53243 8.16 9.57911 8.24667 9.61244ZM10.8333 9.66577C10.7467 9.66577 10.66 9.64578 10.58 9.61244C10.4933 9.57911 10.4267 9.53243 10.36 9.47243L10.28 9.37243C10.2533 9.33243 10.2333 9.29244 10.22 9.25244C10.2 9.21244 10.1867 9.17244 10.18 9.13244C10.1733 9.08578 10.1667 9.0391 10.1667 8.9991C10.1667 8.82577 10.24 8.65244 10.36 8.52577C10.4267 8.46577 10.4933 8.41909 10.58 8.38576C10.8267 8.27909 11.12 8.33911 11.3067 8.52577C11.4267 8.65244 11.5 8.82577 11.5 8.9991C11.5 9.0391 11.4933 9.08578 11.4867 9.13244C11.48 9.17244 11.4667 9.21244 11.4467 9.25244C11.4333 9.29244 11.4133 9.33243 11.3867 9.37243C11.3733 9.39465 11.3541 9.41687 11.3348 9.43909C11.3252 9.4502 11.3156 9.46132 11.3067 9.47243C11.18 9.59243 11.0067 9.66577 10.8333 9.66577ZM5.91333 11.946C5.99333 11.9793 6.08 11.9993 6.16667 11.9993C6.34 11.9993 6.51333 11.926 6.64 11.806C6.76 11.6793 6.83333 11.506 6.83333 11.3326C6.83333 11.1593 6.76 10.986 6.64 10.8593C6.39333 10.6126 5.94 10.6126 5.69333 10.8593C5.63333 10.9193 5.58667 10.9926 5.55333 11.0793C5.52 11.1593 5.5 11.246 5.5 11.3326C5.5 11.506 5.57333 11.6793 5.69333 11.806C5.76 11.866 5.83333 11.9127 5.91333 11.946ZM8.5 11.9993C8.32667 11.9993 8.15333 11.926 8.02667 11.806C7.90667 11.6793 7.83333 11.506 7.83333 11.3326C7.83333 11.246 7.85333 11.1593 7.88667 11.0793C7.92 10.9926 7.96667 10.9193 8.02667 10.8593C8.27333 10.6126 8.72667 10.6126 8.97333 10.8593C9.03333 10.9193 9.08 10.9926 9.11333 11.0793C9.14667 11.1593 9.16667 11.246 9.16667 11.3326C9.16667 11.506 9.09333 11.6793 8.97333 11.806C8.84667 11.926 8.67333 11.9993 8.5 11.9993ZM10.36 11.8058C10.4867 11.9258 10.66 11.9991 10.8333 11.9991C11.0067 11.9991 11.18 11.9258 11.3067 11.8058C11.4267 11.6791 11.5 11.5058 11.5 11.3325C11.5 11.1591 11.4267 10.9858 11.3067 10.8591C11.2956 10.8502 11.2844 10.8406 11.2733 10.831L11.2733 10.831C11.2511 10.8117 11.2289 10.7925 11.2067 10.7791C11.1667 10.7525 11.1267 10.7325 11.0867 10.7191C11.0467 10.6991 11.0067 10.6858 10.96 10.6791C10.7467 10.6325 10.5133 10.7058 10.36 10.8591C10.3 10.9191 10.2533 10.9925 10.22 11.0791C10.1867 11.1591 10.1667 11.2458 10.1667 11.3325C10.1667 11.4191 10.1867 11.5058 10.22 11.5858C10.2533 11.6725 10.3 11.7458 10.36 11.8058Z"
          fill="#737373"
        />
      </svg>
    ),
  }

  return (
    <FormControl fullWidth {...propsContainer} error={isError}>
      {label && labelOutline === true && <Label required={requiredLabel}>{label}</Label>}
      <LocalizationProvider dateAdapter={AdapterMoment}>
        {!isRange ? (
          <DatePickerMUI
            value={date === undefined ? null : date}
            renderInput={(params) => renderInput(params, label, placeholder)}
            components={components}
            onChange={(v) => {
              v = v.isValid() ? v.toDate() : undefined
              onChangeFormValue(v)
            }}
            minDate={minDate}
            maxDate={maxDate}
            // slots={{
            //     // Override default <ActionBar /> with a custom one
            //     actionBar: CustomActionBar,
            // }}
            slotProps={{
              // pass props `actions={['clear']}` to the actionBar slot
              actionBar: { actions: ['clear'] },
            }}
          />
        ) : (
          <Stack direction="row" alignItems="center">
            <DatePickerMUI
              maxDate={endDate}
              value={startDate === undefined ? null : startDate}
              renderInput={(params) => renderInput(params, label, placeholder)}
              components={components}
              onChange={(v) => {
                v = v.isValid() ? v.toDate() : undefined
                setStartDate(v)
                onChangeFormValue(v === undefined || endDate === undefined ? undefined : [v, endDate])
              }}
            />
            <Typography mx={1}>{'-'}</Typography>
            <DatePickerMUI
              minDate={startDate}
              value={endDate === undefined ? null : endDate}
              renderInput={(params) => renderInput(params, labelEndDate || label, placeholderEndDate || placeholder)}
              components={components}
              onChange={(v) => {
                v = v.isValid() ? v.toDate() : undefined
                onChangeFormValue(v === undefined || startDate === undefined ? undefined : [startDate, v])
              }}
            />
          </Stack>
        )}
      </LocalizationProvider>
      {isError && <LabelError>{error}</LabelError>}
    </FormControl>
  )
}

/**
 * @param {IDatePickerProps & FieldConfig} props
 * @returns
 */
export const FieldDatePicker = (props) => <Field {...props} component={DatePicker} />
